/* eslint-disable no-console */
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as BaseApolloProvider,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { FunctionComponent, PropsWithChildren, useMemo } from 'react';

import { getEnv } from '../config/env';
import { getApiErrorMessage } from '../error/services/error.service';
import { firebaseAuth } from '../firebase/firebase.service';
import { useToast } from '../toast/Toast.context';

const httpLink = createHttpLink({
  uri: `${getEnv('REACT_APP_API_URL')}${getEnv('REACT_APP_GRAPHQL_ENDPOINT')}`,
});

const authLink = setContext(async (_, { headers }) => {
  const token = await firebaseAuth.currentUser?.getIdToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : undefined,
    },
  };
});

// Configure the cache
const cache = new InMemoryCache({
  typePolicies: {
    Event: {
      fields: {
        startAt: {
          read: (dateString) => new Date(dateString),
        },
        endAt: {
          read: (dateString) => new Date(dateString),
        },
      },
    },
    Milestone: {
      fields: {
        startAt: {
          read: (dateString) => new Date(dateString),
        },
        endAt: {
          read: (dateString) => new Date(dateString),
        },
      },
    },
  },
});

export const ApolloProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const { showToast } = useToast();

  const apolloClient = useMemo(() => {
    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      const context = operation.getContext();
      if (graphQLErrors)
        graphQLErrors.map((graphqlError) => {
          const errorMessage = getApiErrorMessage(graphqlError);
          console.error(graphqlError, errorMessage);

          if (!context.noToast) {
            showToast({ type: 'error', message: errorMessage });
          }
        });
      if (networkError) {
        console.error(networkError);

        if (!context.noToast) {
          showToast({
            type: 'error',
            message:
              'Impossible de se connecter au serveur, vérifiez votre connexion',
          });
        }
      }
    });

    const client = new ApolloClient({
      cache,
      link: ApolloLink.from([errorLink, authLink, httpLink]),
    });

    return client;
  }, [showToast]);

  return (
    <BaseApolloProvider client={apolloClient}>{children}</BaseApolloProvider>
  );
};
